import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { Iconify } from '@/shared/components/iconify';

import { GENERAL_GEM_ACCENT } from './chat-sidebar';

interface ChatTopbarProps {
  title: string;
  gemBadge?: string;
  gemAccent?: string;
  /**
   * MenuButton handler — on mobile opens/closes the drawer, on desktop flips
   * between vertical and mini. ChatLayout owns the mobile/desktop branching.
   * Matches v1's pattern where the same hamburger drives both nav modes.
   */
  onSidebarToggle: () => void;
  /** Opens the Share dialog. Owned by ChatLayout (same pattern as GemSwitchDialog). */
  onShareClick: () => void;
  /** Whether a conversation is loaded — the Share button is disabled when empty. */
  canShare: boolean;
}

export function ChatTopbar({
  title,
  gemBadge,
  gemAccent = GENERAL_GEM_ACCENT,
  onSidebarToggle,
  onShareClick,
  canShare,
}: ChatTopbarProps) {
  const { t } = useTranslation('chat');

  return (
    <Stack
      component="header"
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 3,
        py: 1.5,
        borderBottom: 1,
        borderBottomColor: 'divider',
        backgroundColor: 'background.default',
        flexShrink: 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
        <IconButton
          size="small"
          onClick={onSidebarToggle}
          aria-label={t('sidebar.toggleAria')}
          sx={{
            color: 'text.secondary',
            flexShrink: 0,
            // Desktop has a persistent sidebar with its own toggle, so this
            // control is only the drawer trigger on mobile. Hidden from `md` up.
            display: { xs: 'inline-flex', md: 'none' },
          }}
        >
          <Iconify icon="custom:sidebar-panel-outline" width={18} />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Typography>
          {gemBadge && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: gemAccent,
                backgroundColor: `${gemAccent}1A`,
                borderRadius: (t) => `${t.shape.borderRadius}px`,
                flexShrink: 0,
              }}
            >
              {gemBadge}
            </Box>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Tooltip title={t('topbar.actions.share')}>
          {/* span wrapper keeps the tooltip working when the button is disabled */}
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <IconButton
              size="small"
              onClick={onShareClick}
              disabled={!canShare}
              aria-label={t('topbar.actions.share')}
              sx={{ color: 'text.secondary' }}
            >
              <Iconify icon="solar:share-bold" width={18} />
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
